import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import { useLocation } from 'react-router-dom';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import './style.css';

/**
 * Exit-Intent Popup Component
 * 
 * Displays a lead capture popup when users show exit intent (mouse leaving viewport)
 * or when they've scrolled past a threshold.
 * 
 * Only displays on specific pages: /, /signup, /level-test, /pricing, /subscribe
 * Uses different thresholds for paid vs organic traffic.
 */
const ExitIntentPopup = ({
    onSubmit,
    leadMagnet = "Arabic Alphabet Learning Kit",
    enabled = true
}) => {
    const location = useLocation();
    const { t } = useTranslation();
    const {
        getTrafficSource,
        trackExitIntentTriggered,
        trackExitPopupShown,
        trackExitPopupConverted,
        trackExitPopupDismissed,
        trackEvent
    } = useAnalytics();

    const [isVisible, setIsVisible] = useState(false);
    const [hasTriggered, setHasTriggered] = useState(false);
    const [scrollDepth, setScrollDepth] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        whatsapp: '',
        email: ''
    });
    const [errors, setErrors] = useState({});

    // Debounce fields for partial capture
    const [debouncedName] = useDebounce(formData.name, 500);
    const [debouncedEmail] = useDebounce(formData.email, 500);
    const [debouncedWhatsapp] = useDebounce(formData.whatsapp, 500);
    // identify and trackEvent destructuring removed from here as they are merged above

    // Track partial entry
    // Track partial entry
    useEffect(() => {
        if (debouncedName && debouncedName.length > 2) {
            trackEvent('exit_intent_name_entered', { name: debouncedName });
        }
    }, [debouncedName, trackEvent]);

    useEffect(() => {
        if (debouncedEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(debouncedEmail)) {
            trackEvent('exit_intent_email_entered', { email: debouncedEmail });
        }
    }, [debouncedEmail, formData.name, trackEvent]);

    useEffect(() => {
        if (debouncedWhatsapp && debouncedWhatsapp.length > 8) {
            trackEvent('exit_intent_phone_entered', { phone: debouncedWhatsapp });
        }
    }, [debouncedWhatsapp, formData.name, trackEvent]);

    // Pages where exit-intent should be active
    const enabledPages = ['/', '/signup', '/level-test', '/pricing', '/subscribe'];
    const isEnabledPage = enabledPages.some(page =>
        location.pathname === page || location.pathname.startsWith(page + '/')
    );

    // Smart exit intent state
    const [pageLoadTime] = useState(Date.now());
    const [lastVisibilityChange, setLastVisibilityChange] = useState(null);
    const [isInVerificationFlow, setIsInVerificationFlow] = useState(false);

    // Detect if user is in email verification flow (checking sessionStorage)
    useEffect(() => {
        const checkVerificationFlow = () => {
            const signupSession = sessionStorage.getItem('signup_session');
            if (signupSession) {
                try {
                    const session = JSON.parse(signupSession);
                    // If user is on step 3 with email method, they're likely checking email
                    if (session.step === 3 && session.verificationMethod === 'email') {
                        setIsInVerificationFlow(true);
                        return;
                    }
                } catch (e) { }
            }
            setIsInVerificationFlow(false);
        };

        checkVerificationFlow();
        // Re-check when page visibility changes
        const handleVisibility = () => checkVerificationFlow();
        document.addEventListener('visibilitychange', handleVisibility);
        return () => document.removeEventListener('visibilitychange', handleVisibility);
    }, [location.pathname]);

    // Track visibility changes to detect tab switches vs real exits
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                setLastVisibilityChange(Date.now());
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, []);

    // Get scroll threshold based on traffic source
    const getScrollThreshold = useCallback(() => {
        const trafficSource = getTrafficSource();
        // More aggressive for organic (30%), standard for paid (50%)
        return trafficSource === 'organic' ? 30 : 50;
    }, [getTrafficSource]);

    // Track scroll depth
    useEffect(() => {
        if (!enabled || !isEnabledPage) return;

        const handleScroll = () => {
            const scrollTop = window.scrollY;
            const docHeight = document.documentElement.scrollHeight - window.innerHeight;
            const currentDepth = Math.round((scrollTop / docHeight) * 100);
            setScrollDepth(currentDepth);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [enabled, isEnabledPage]);

    // Smart exit intent detection
    const shouldTriggerExitIntent = useCallback(() => {
        // 1. Don't trigger if user is in email verification flow
        if (isInVerificationFlow) {
            console.log('[ExitIntent] Blocked: User in email verification flow');
            return false;
        }

        // 2. Require minimum 10 seconds on page (reduces accidental triggers)
        const timeOnPage = Date.now() - pageLoadTime;
        if (timeOnPage < 10000) {
            console.log('[ExitIntent] Blocked: Less than 10s on page');
            return false;
        }

        // 3. Don't trigger within 3 seconds of a tab switch (user coming back from email)
        if (lastVisibilityChange && (Date.now() - lastVisibilityChange) < 3000) {
            console.log('[ExitIntent] Blocked: Recent tab switch detected');
            return false;
        }

        // 4. Don't trigger on signup/login pages when forms have content
        if (location.pathname === '/signup' || location.pathname === '/login') {
            const inputs = document.querySelectorAll('input[type="text"], input[type="email"], input[type="password"], input[type="tel"]');
            for (let input of inputs) {
                if (input.value && input.value.length > 0) {
                    console.log('[ExitIntent] Blocked: Form has content on auth page');
                    return false;
                }
            }
        }

        // 5. Require scroll threshold to be met
        if (scrollDepth < getScrollThreshold()) {
            console.log('[ExitIntent] Blocked: Scroll threshold not met');
            return false;
        }

        return true;
    }, [isInVerificationFlow, pageLoadTime, lastVisibilityChange, location.pathname, scrollDepth, getScrollThreshold]);

    // Detect exit intent (mouse leaving viewport from top)
    useEffect(() => {
        if (!enabled || !isEnabledPage || hasTriggered) return;

        const handleMouseLeave = (e) => {
            // Only trigger if mouse leaves from top of viewport
            if (e.clientY <= 0 && shouldTriggerExitIntent()) {
                triggerPopup();
            }
        };

        document.addEventListener('mouseleave', handleMouseLeave);
        return () => document.removeEventListener('mouseleave', handleMouseLeave);
    }, [enabled, isEnabledPage, hasTriggered, shouldTriggerExitIntent]);

    // Check if already shown in this session
    useEffect(() => {
        const shown = sessionStorage.getItem('exit_popup_shown');
        if (shown) {
            setHasTriggered(true);
        }
    }, []);

    const triggerPopup = () => {
        if (hasTriggered) return;

        const trafficSource = getTrafficSource();
        trackExitIntentTriggered(location.pathname, scrollDepth);
        trackExitPopupShown(leadMagnet);

        setIsVisible(true);
        setHasTriggered(true);
        sessionStorage.setItem('exit_popup_shown', 'true');
    };

    const handleClose = () => {
        trackExitPopupDismissed();
        setIsVisible(false);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = t('exitIntent.nameRequired');
        }

        // Require either WhatsApp or email
        if (!formData.whatsapp.trim() && !formData.email.trim()) {
            newErrors.whatsapp = t('exitIntent.contactRequired');
            newErrors.email = t('exitIntent.contactRequired');
        }

        // Validate email format if provided
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = t('exitIntent.invalidEmail');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsSubmitting(true);

        try {
            // Track conversion
            trackExitPopupConverted(
                leadMagnet,
                formData.email,
                formData.whatsapp,
                formData.name
            );

            // Persist lead to backend
            try {
                const API_BASE = process.env.NODE_ENV === 'production' ? '/api' : (process.env.REACT_APP_API_URL || 'http://localhost:8000');
                await fetch(`${API_BASE}/api/v1/leads`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        name: formData.name,
                        email: formData.email,
                        phone: formData.whatsapp,
                        source: 'exit_intent',
                    })
                });
            } catch (apiError) {
                console.error('Lead API error (non-blocking):', apiError);
            }

            // Call parent onSubmit handler
            if (onSubmit) {
                await onSubmit(formData);
            }

            // Close popup after success
            setIsVisible(false);

        } catch (error) {
            console.error('Exit popup submit error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isVisible || !enabled || !isEnabledPage) {
        return null;
    }

    return (
        <div className="exit-intent-overlay" onClick={handleClose}>
            <div className="exit-intent-popup" onClick={e => e.stopPropagation()}>

                {/* Left Side: Visual & Value */}
                <div className="exit-intent-left">
                    <div className="gift-visual">
                        <span className="floating-emoji">🎁</span>
                        <div className="shine-effect"></div>
                    </div>
                    <div className="value-props">
                        <h3>{t('exitIntent.freeLearningKit')}</h3>
                        <ul>
                            <li>✓ {t('exitIntent.feature1')}</li>
                            <li>✓ {t('exitIntent.feature2')}</li>
                            <li>✓ {t('exitIntent.feature3')}</li>
                        </ul>
                    </div>
                </div>

                {/* Right Side: Form */}
                <div className="exit-intent-right">
                    <button className="exit-intent-close" onClick={handleClose}>×</button>

                    <div className="exit-intent-content">
                        <div className="exit-intent-header">
                            <h2>{t('exitIntent.waitDontMiss')}</h2>
                            <p dangerouslySetInnerHTML={{ __html: t('exitIntent.getFreeGuide', { leadMagnet }) }}></p>
                        </div>

                        <form onSubmit={handleSubmit} className="exit-intent-form">
                            <div className="form-group">
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder={t('exitIntent.yourName')}
                                    className={errors.name ? 'error' : ''}
                                    disabled={isSubmitting}
                                />
                                {errors.name && <span className="error-text">{errors.name}</span>}
                            </div>

                            <div className="form-group">
                                <div className="input-with-icon">
                                    <span className="input-icon">📱</span>
                                    <input
                                        type="tel"
                                        name="whatsapp"
                                        value={formData.whatsapp}
                                        onChange={handleInputChange}
                                        placeholder={t('exitIntent.whatsappPlaceholder')}
                                        className={errors.whatsapp ? 'error' : ''}
                                        disabled={isSubmitting}
                                    />
                                </div>
                                {errors.whatsapp && <span className="error-text">{errors.whatsapp}</span>}
                            </div>

                            <div className="divider"><span>{t('exitIntent.or')}</span></div>

                            <div className="form-group">
                                <div className="input-with-icon">
                                    <span className="input-icon">📧</span>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder={t('exitIntent.emailPlaceholder')}
                                        className={errors.email ? 'error' : ''}
                                        disabled={isSubmitting}
                                    />
                                </div>
                                {errors.email && <span className="error-text">{errors.email}</span>}
                            </div>

                            <button
                                type="submit"
                                className="exit-intent-submit"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? t('exitIntent.sending') : `🎁 ${t('exitIntent.sendFreeGuide')}`}
                            </button>

                            <p className="privacy-note">🔒 {t('exitIntent.privacyNote')}</p>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExitIntentPopup;
